import type { Job, JobImageEvidence } from '../types/models';
import type { WebImageType } from './imageUpload';

export interface GalleryImage {
  url: string;
  evidence?: JobImageEvidence;
  displayType: string;
}

const CORRECTION_TYPES: Array<{ value: WebImageType; label: string }> = [
  { value: 'collection', label: 'Collection' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'additional', label: 'Additional' },
  { value: 'courtesy', label: 'Courtesy Car' },
];

const TYPE_LABELS: Record<string, string> = {
  collection: 'Collection', delivery: 'Delivery', courtesy: 'Courtesy Car',
  additional: 'Additional', damage: 'Damage', signature: 'Signature',
  standard: 'Standard', unclassified: 'Other Evidence',
};

const GROUP_ORDER = [
  'collection', 'delivery', 'courtesy', 'additional',
  'damage', 'signature', 'standard', 'unclassified',
];

function groupRank(type: string) {
  const index = GROUP_ORDER.indexOf(type.trim().toLowerCase());
  return index < 0 ? GROUP_ORDER.length : index;
}

export function evidenceCorrectionTypes(courtesyCarRequired: boolean) {
  return CORRECTION_TYPES.filter((type) => courtesyCarRequired || type.value !== 'courtesy');
}

export function evidenceUploadTypes(courtesyCarRequired: boolean) {
  const types = evidenceCorrectionTypes(courtesyCarRequired);
  return GROUP_ORDER.flatMap((value) => types.filter((type) => type.value === value));
}

export function evidenceStoragePath(url: string, bucket: string, legacyBucket?: string): string | null {
  if (url.startsWith('companies/') && !url.includes('?') && !url.includes('#')) return url;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'gs:') return null;
    const parts = parsed.pathname.split('/').filter(Boolean);
    const knownBucket = (value: string) => value === bucket || (!!legacyBucket && value === legacyBucket);
    if (parsed.protocol === 'gs:' && knownBucket(parsed.hostname)) {
      return parts.length ? decodeURIComponent(parts.join('/')) : null;
    }
    if (parsed.hostname === 'storage.googleapis.com' && knownBucket(parts[0])) {
      return parts.length > 1 ? decodeURIComponent(parts.slice(1).join('/')) : null;
    }
    if (parsed.hostname === 'firebasestorage.googleapis.com' &&
        parts[0] === 'v0' && parts[1] === 'b' && knownBucket(parts[2]) && parts[3] === 'o') {
      return parts.length > 4 ? decodeURIComponent(parts.slice(4).join('/')) : null;
    }
  } catch {
    // A legacy URL without a canonical path is not an authenticated evidence reference.
  }
  return null;
}

export function galleryImages(job: Pick<Job, 'typedImages' | 'imageSessions' | 'imageUrls'>, bucket: string, legacyBucket?: string): GalleryImage[] {
  const normalized = (url: string) => url.split(/[?#]/)[0];
  const byUrl = new Map<string, JobImageEvidence>();
  const byPath = new Map<string, JobImageEvidence>();
  for (const evidence of job.typedImages ?? []) {
    if (!evidence) continue;
    if (typeof evidence.url === 'string' && !byUrl.has(normalized(evidence.url))) {
      byUrl.set(normalized(evidence.url), evidence);
    }
    const path = evidence.storagePath || (typeof evidence.url === 'string' ? evidenceStoragePath(evidence.url, bucket, legacyBucket) : null);
    if (path && !byPath.has(path)) byPath.set(path, evidence);
  }

  const seen = new Set<string>();
  const images: GalleryImage[] = [];
  function add(value: unknown, fallbackType?: string | null) {
    if (typeof value !== 'string' || !value.trim()) return;
    const path = evidenceStoragePath(value, bucket, legacyBucket);
    const evidence = byUrl.get(normalized(value)) || (path ? byPath.get(path) : undefined);
    const key = evidence?.storagePath || path || normalized(value);
    if (seen.has(key)) return;
    seen.add(key);
    images.push({
      url: evidence?.url || value,
      evidence,
      displayType: evidence?.type || fallbackType || 'unclassified',
    });
  }
  for (const session of job.imageSessions ?? []) {
    for (const url of session.imageUrls ?? []) add(url, session.type);
  }
  for (const evidence of job.typedImages ?? []) add(evidence?.url || evidence?.storagePath, evidence?.type);
  for (const url of job.imageUrls ?? []) add(url);
  return images.sort((a, b) => groupRank(a.displayType) - groupRank(b.displayType));
}

export function galleryGroups(images: GalleryImage[]) {
  const grouped = new Map<string, Array<{ image: GalleryImage; index: number }>>();
  images.forEach((image, index) => {
    const type = image.displayType.trim().toLowerCase() || 'unclassified';
    const entries = grouped.get(type) ?? [];
    entries.push({ image, index });
    grouped.set(type, entries);
  });
  return [...grouped.entries()]
    .sort(([a], [b]) => groupRank(a) - groupRank(b) || a.localeCompare(b))
    .map(([type, entries]) => ({
      type,
      label: TYPE_LABELS[type] || type.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()),
      entries,
    }));
}

export async function runEvidenceActions<T>(
  targets: T[],
  action: (target: T) => Promise<unknown>,
  onProgress: (completed: number, total: number) => void,
  concurrency: 1 | 3 = 3
) {
  let completed = 0;
  let succeeded = 0;
  const failures: string[] = [];
  onProgress(0, targets.length);
  for (let start = 0; start < targets.length; start += concurrency) {
    await Promise.all(targets.slice(start, start + concurrency).map(async (target) => {
      try {
        await action(target);
        succeeded += 1;
      } catch (error) {
        failures.push(error instanceof Error ? error.message : String(error));
      } finally {
        completed += 1;
        onProgress(completed, targets.length);
      }
    }));
  }
  return { succeeded, total: targets.length, failures };
}
